/**
 * RF-SLC20260926-004 — UI-04-02 — Dialog `Detalhes de Fatura`.
 *
 * Componente interno do `FinanceiroFaturacaoAutomaticaListView`; não é
 * registado como componente AngularJS (não tem template público).
 *
 * Apresenta os campos do pedido (cabeçalho, cliente, linhas, ligação ao
 * registo de origem por departamento) e expõe os botões de ação contextuais:
 *   - "Transferir fatura"   → ativo quando ficheiro_uuid existe (e estado != 3).
 *   - "Tentar novamente"    → ativo apenas em estado 3 (Erro definitivo) e
 *                              sem id_documento_erp.
 *
 * Spec: specs/01_requirements/RF-SLC20260926-004-vista-controlo.md
 * UI:   specs/04_ux_ui/web/ui-04-01-vista-controlo.md §UI-04-02
 */
import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Table as MUITable, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
const ESTADO_ERRO_DEFINITIVO = 3;
const CORES_ESTADO = {
    1: { fundo: '#dee0e3', texto: '#807272', label: 'Aguarda Processamento' },
    2: { fundo: '#fff5cc', texto: '#FFA600', label: 'Em Processamento' },
    3: { fundo: '#fceaea', texto: '#DC2626', label: 'Erro definitivo' },
    4: { fundo: '#e6f9eb', texto: '#00BF33', label: 'Concluído' }
};
const txt = (v) => {
    if (v === null || v === undefined || ('' + v).trim() === '')
        return '-';
    return '' + v;
};
const dataHora = (v, format) => {
    if (!v)
        return '-';
    const m = moment(v);
    return m.isValid() ? m.format(format) : '-';
};
/**
 * Renderiza a descrição da linha de fatura quebrando linhas. O `item_descricao`
 * pode conter quebras como `<br>`, `\n` literal (barra+n, gravado pelo módulo Gás
 * — ver OQ-SLC20260926-031) ou newline real. Normaliza tudo para quebra real e devolve nós
 * React com `<br/>` (sem `dangerouslySetInnerHTML`).
 */
const descricaoComQuebras = (desc) => {
    if (!desc || !desc.trim())
        return '-';
    const partes = desc
        .replace(/<br\s*\/?>/gi, '\n') // <br> / <br/> → newline
        .replace(/\\r\\n|\\n|\\r/g, '\n') // "\r\n"/"\n"/"\r" literais (barra+n) → newline
        .replace(/\r\n|\r/g, '\n') // CRLF/CR → newline
        .split('\n');
    return partes.map((linha, i) => (<React.Fragment key={i}>
      {linha}
      {i < partes.length - 1 && <br />}
    </React.Fragment>));
};
/** Devolve a label + valor da FK de origem em função do departamento. */
const ligacaoOrigem = (p) => {
    if (p.gasProcessoId)
        return { label: 'Processo Gás', valor: '#' + p.gasProcessoId };
    if (p.iePedidoId)
        return { label: 'Pedido IE', valor: '#' + p.iePedidoId };
    if (p.formacaoInscricaoId)
        return { label: 'Inscrição Formação', valor: '#' + p.formacaoInscricaoId };
    if (p.formacaoInscricaoEmpresaId)
        return { label: 'Inscrição Empresa (Formação)', valor: '#' + p.formacaoInscricaoEmpresaId };
    return { label: 'Pedido de origem', valor: '-' };
};
// Mini-componente para par "label / valor" em grid.
// `component='div'` no valor: o valor pode ser um nó de bloco (ex.: `<Chip>` do
// campo "Estado", que renderiza um `<div>`). Por omissão a Typography seria um
// `<p>` e teríamos `<div>` dentro de `<p>` (validateDOMNesting inválido).
const Campo = ({ label, valor, cols = 6 }) => (<Grid item xs={12} sm={cols}>
    <Typography variant='caption' color='text.secondary' component='div'>
      {label}
    </Typography>
    <Typography variant='body2' component='div'>{valor}</Typography>
  </Grid>);
const DetailsDialog = ({ pedido, onClose, onTentarNovamente, onTransferir, podeEditar = false, onGuardarEReprocessar }) => {
    const podeRetentar = pedido.estadoId === ESTADO_ERRO_DEFINITIVO && !pedido.documentoErp;
    const emErro = pedido.estadoId === ESTADO_ERRO_DEFINITIVO;
    const podeTransferir = !!pedido.ficheiroUuid && pedido.estadoId !== ESTADO_ERRO_DEFINITIVO;
    const corEstado = pedido.estadoId ? CORES_ESTADO[pedido.estadoId] : null;
    const origem = ligacaoOrigem(pedido);
    // Edição só é possível em Erro definitivo, sem fatura emitida e com o modo ligado (RB-A7-02/03).
    const emEdicao = podeEditar && pedido.estadoId === ESTADO_ERRO_DEFINITIVO && !pedido.documentoErp;
    // Estado do formulário dos campos editáveis (RB-A7-01), inicializado do pedido.
    const [form, setForm] = useState({});
    useEffect(() => {
        setForm({
            nomeCliente: pedido.nomeCliente ?? '',
            nif: pedido.nif ?? '',
            email: pedido.email ?? '',
            telefone: pedido.telefone ?? '',
            morada: pedido.morada ?? '',
            codPostal: pedido.codPostal ?? '',
            codPostalLocalidade: pedido.codPostalLocalidade ?? '',
            concelho: pedido.concelho ?? '',
            distrito: pedido.distrito ?? ''
        });
    }, [pedido]);
    const setCampo = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));
    // Campo editável (TextField) ou leitura (Typography), conforme `emEdicao`.
    // Função invocada inline (NÃO um componente `<CampoEditavel/>`): definir um
    // componente dentro do render recria o tipo a cada render e o React remontaria
    // os TextField, perdendo o foco a cada tecla.
    const campoEditavel = (label, campo, valor, cols = 6) => {
        if (!emEdicao)
            return <Campo label={label} valor={txt(valor)} cols={cols}/>;
        return (<Grid item xs={12} sm={cols} key={campo}>
        <TextField fullWidth size='small' variant='standard' label={label} value={form[campo] ?? ''} onChange={setCampo(campo)}/>
      </Grid>);
    };
    return (<Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        {emEdicao ? 'Editar Fatura' : 'Detalhes da Fatura'} <span style={{ color: '#999', fontWeight: 400 }}>#{pedido.id}</span>
      </DialogTitle>

      <DialogContent dividers>
        {/* Estado + ligação ao registo de origem */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Campo label='Estado' valor={corEstado ? (<Chip size='small' label={corEstado.label} sx={{ backgroundColor: corEstado.fundo, color: corEstado.texto, fontWeight: 500 }}/>) : (txt(pedido.estado?.designacao))}/>
          <Campo label={origem.label} valor={txt(origem.valor)}/>
        </Grid>

        <Divider sx={{ my: 1 }}/>

        {/* Cabeçalho do pedido */}
        <Typography variant='subtitle1' sx={{ mt: 1, mb: 1 }}>
          Pedido
        </Typography>
        <Grid container spacing={2}>
          <Campo label='Departamento' valor={txt(pedido.departamento?.designacao)}/>
          <Campo label='Processo' valor={txt(pedido.numProcesso)}/>
          <Campo label='Funcionário' valor={txt(pedido.funcionario?.name)}/>
          <Campo label='Data Pedido' valor={dataHora(pedido.dataRegisto, 'DD/MM/YYYY')}/>
          <Campo label='Data Fatura' valor={dataHora(pedido.dataCriacao, 'DD/MM/YYYY HH:mm:ss')}/>
          {pedido.documentoErp && <Campo label='Nº Fatura (ERP)' valor={txt(pedido.documentoErp)}/>}
        </Grid>

        <Divider sx={{ my: 2 }}/>

        {/* Cliente — editável em modo A7 (RB-A7-01) */}
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          Cliente
        </Typography>
        <Grid container spacing={2}>
          {campoEditavel('Nome', 'nomeCliente', pedido.nomeCliente)}
          {campoEditavel('NIF', 'nif', pedido.nif)}
          {campoEditavel('Telefone', 'telefone', pedido.telefone)}
          {campoEditavel('Email', 'email', pedido.email)}
          {campoEditavel('Morada', 'morada', pedido.morada, 12)}
          {campoEditavel('Código-Postal', 'codPostal', pedido.codPostal)}
          {campoEditavel('Localidade', 'codPostalLocalidade', pedido.codPostalLocalidade)}
          {campoEditavel('Concelho', 'concelho', pedido.concelho)}
          {campoEditavel('Distrito', 'distrito', pedido.distrito)}
        </Grid>

        <Divider sx={{ my: 2 }}/>

        {/* Linhas de fatura */}
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          Linhas de fatura
        </Typography>
        {(pedido.linhas || []).length === 0 ? (<Typography variant='body2' color='text.secondary'>
            -
          </Typography>) : (<MUITable size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell>Código Primavera</TableCell>
                <TableCell align='right'>Quantidade</TableCell>
                <TableCell align='right'>Preço Unitário</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(pedido.linhas || []).map(l => (<TableRow key={l.id}>
                  <TableCell>
                    {/* item_descricao pode conter quebras `<br>`, `\n` literal ou newline real. */}
                    {descricaoComQuebras(l.itemDescricao)}
                  </TableCell>
                  <TableCell>{txt(l.itemCodigoPrimavera)}</TableCell>
                  <TableCell align='right'>{txt(l.itemQuantidade)}</TableCell>
                  <TableCell align='right'>
                    {l.itemPrecoNet != null ? Number(l.itemPrecoNet).toFixed(2) + ' €' : '-'}
                  </TableCell>
                </TableRow>))}
            </TableBody>
          </MUITable>)}

        {/* Histórico de processamento (`erro_descricao`) — cumulativo e nunca
            apagado (CR-SLD20180014-0007), por isso também aparece em pedidos já
            concluídos. Vermelho só enquanto o pedido está em Erro definitivo.
            Uma entrada por linha: as entradas começam sempre por `[...]`. */}
        {pedido.erroDescricao && (<>
            <Divider sx={{ my: 2 }}/>
            <Typography variant='subtitle1' sx={{ mb: 1 }} color={emErro ? 'error' : undefined}>
              Histórico de processamento
            </Typography>
            <Box sx={{
                p: 1.5,
                backgroundColor: emErro ? '#fceaea' : '#f5f5f5',
                color: emErro ? '#DC2626' : 'inherit',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.85em'
            }}>
              {pedido.erroDescricao.split(/ ; (?=\[)/).join('\n')}
            </Box>
          </>)}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Box>
          {/* "Tentar novamente" só quando é mesmo aplicável (Erro definitivo, não
            emitida) e fora do modo de edição — no modo de edição o "Guardar e
            reprocessar" já reprocessa; numa fatura já realizada não aparece. */}
          {!emEdicao && podeRetentar && (<Button variant='outlined' onClick={onTentarNovamente} sx={{ mr: 1 }}>
              Tentar novamente
            </Button>)}
          {/* A7 — guarda os campos de faturação e repõe na queue (ação atómica, RB-A7-04). */}
          {emEdicao && onGuardarEReprocessar && (<Button variant='contained' onClick={() => onGuardarEReprocessar(form)} sx={{ mr: 1 }}>
              Guardar e reprocessar
            </Button>)}
          {/* "Transferir fatura" só quando a fatura foi emitida (tem PDF e não está
            em erro) — não aparece em pedidos por emitir/em erro. */}
          {podeTransferir && (<Button variant='contained' onClick={onTransferir}>
              Transferir fatura
            </Button>)}
        </Box>
      </DialogActions>
    </Dialog>);
};
export default DetailsDialog;
